import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CartProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0, count: 0 });
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setCart(res.data);
    } catch {
      setCart({ items: [], total: 0, count: 0 });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    const res = await axios.post(`${API_URL}/api/cart/items`, { product_id: productId, quantity }, { headers });
    setCart(res.data);
    return res.data;
  };

  const updateQuantity = async (productId, quantity) => {
    const res = await axios.put(`${API_URL}/api/cart/items/${productId}`, { quantity }, { headers });
    setCart(res.data);
    return res.data;
  };

  const removeItem = async (productId) => {
    const res = await axios.delete(`${API_URL}/api/cart/items/${productId}`, { headers });
    setCart(res.data);
    return res.data;
  };

  const clearCart = async () => {
    const res = await axios.delete(`${API_URL}/api/cart`, { headers });
    setCart(res.data);
    return res.data;
  };

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, updateQuantity, removeItem, clearCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
